import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function Chip({ state, label, className = "" }) {
  const map = {
    ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    down: "bg-red-500/15 text-red-300 border-red-500/30",
    missing: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    local: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    misconfigured: "bg-red-500/15 text-red-300 border-red-500/30",
    p0: "bg-red-500/15 text-red-300 border-red-500/30",
    p1: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    p2: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    p3: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  };
  return <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border ${map[state] || "border-[var(--fop-border)]"} ${className}`}>{label || state}</span>;
}

export function Stat({ label, value, sub, accent }) {
  const accentMap = {
    red: "border-red-500/40 bg-red-500/5",
    amber: "border-amber-500/40 bg-amber-500/5",
    green: "border-emerald-500/40 bg-emerald-500/5",
  };
  const cls = accent ? accentMap[accent] : "border-[var(--fop-border)]";
  return (
    <div className={`fop-surface-2 rounded-lg p-3 border ${cls}`} data-testid={`stat-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">{label}</div>
      <div className="font-display text-2xl text-[var(--fop-text)] mt-0.5">{value}</div>
      {sub && <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-0.5">{sub}</div>}
    </div>
  );
}

export function SectionHeader({ icon, title, action }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] inline-flex items-center gap-1.5">
        {icon} {title}
      </div>
      {action}
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="p-8 font-mono text-xs text-[var(--fop-text-3)] inline-flex items-center gap-2">
      <Loader2 size={14} className="animate-spin" /> {label}
    </div>
  );
}

export function Empty({ children }) {
  return <div className="p-6 text-[11px] text-[var(--fop-text-4)] italic text-center">{children}</div>;
}

// Hard-Gate P1 (2026-09-06) — every /platform/* endpoint here now requires
// MFA enrollment (`require_platform_admin` -> `_mfa_gate`). Previously a
// 403/any other error just toasted, leaving `data` (or `loading`) stuck
// forever with no terminal state — this is the single, shared, correct
// owner for classifying + surfacing that instead of an endless spinner.
export function usePlatformFetch(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null); // null | "mfa_required" | "error"
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get(path).then((r) => {
      if (!cancelled) setData(r.data);
    }).catch((e) => {
      if (cancelled) return;
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && typeof detail === "string" && detail.toLowerCase().includes("mfa enrollment is required")) {
        setError("mfa_required");
      } else {
        setError("error");
        toast.error(`${path}: ${detail || "load failed"}`);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick]);

  return { data, error, retry: () => setTick((t) => t + 1) };
}

export function LoadError({ error, retry, label = "This section" }) {
  if (error === "mfa_required") {
    return (
      <div className="p-8 text-center" data-testid="platform-mfa-required">
        <ShieldAlert size={22} className="text-amber-400 mx-auto mb-2" />
        <div className="text-sm text-[var(--fop-text-2)] mb-3">Two-factor authentication is required for this action.</div>
        <Link to="/settings" className="btn btn-primary text-xs inline-flex" data-testid="platform-mfa-enroll-link">
          Set up two-factor authentication
        </Link>
      </div>
    );
  }
  return (
    <div className="p-8 text-center" data-testid="platform-load-error">
      <div className="text-sm text-[var(--fop-text-2)] mb-3">{label} couldn't load.</div>
      <button onClick={retry} className="btn btn-ghost text-xs" data-testid="platform-load-retry">Retry</button>
    </div>
  );
}

export function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "—";
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
